'use client';

import { Form } from '@form/Form';
import { FormDivider, FormInput } from '@form/index';
import useCurrency from '@hooks/useCurrency';
import { useMemo } from 'react';
import { FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { centsToAmountInput } from '../amountField';
import AccountFormInput from '../Account/AccountFormInput';
import CostCenterFormInput from '../CostCenter/CostCenterFormInput';
import { useTaxCodeOptions } from '../useTaxCodeOptions';
import type { ExternalInvoiceData } from './types';

interface Props {
    data: ExternalInvoiceData;
    isSubmitting: boolean;
    submitLabel: string;
    /** Rendered inside the form so the payment rail can read the live field values. */
    rail?: React.ReactNode;
    onSubmit: (values: FieldValues) => void | Promise<any>;
}

/**
 * The receipt's fields, grouped the way the document reads.
 *
 * The two sections up top are what a receipt actually shows — who sent it, what it books to.
 * Address, bank details and cost centres are real columns but rarely touched during capture, so
 * they sit in a folded section instead of pushing the amount below the fold.
 *
 * Deliberately keyed by the caller per document — together with setDefaultValuesOnRerender={false}
 * that is what stops an arriving websocket signal from overwriting half-typed input.
 */
const BelegForm = ({ data, isSubmitting, submitLabel, rail, onSubmit }: Props) => {
    const { t } = useTranslation('invoice');
    const { currencies } = useCurrency();
    const taxCodeOptions = useTaxCodeOptions();

    const documentTypeOptions = useMemo(
        () => [
            { key: 'expense', label: t('belegAssistant.documentType.expense') },
            { key: 'credit', label: t('belegAssistant.documentType.credit') }
        ],
        [t]
    );

    // Memoised: the shared Form re-seeds fields whenever this object's identity changes.
    const defaultValues = useMemo(
        () => ({
            ...data,
            // A select rather than a checkbox, the way the document itself names it.
            document_type: data.is_credit ? 'credit' : 'expense',
            currency: data.currency ?? 'EUR',
            user_invoice_account_number: data.account_number ?? null,
            // The column is cents, the field is euro.
            value: centsToAmountInput(data.value)
        }),
        [data]
    );

    return (
        <Form
            onSubmit={onSubmit}
            defaultValues={defaultValues}
            setDefaultValuesOnRerender={false}
            className="tw-flex tw-h-full tw-min-h-0 tw-flex-col tw-gap-4"
        >
            <div className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-overflow-y-auto tw-pr-1">
                <FormDivider title={t('belegAssistant.sections.document')}>
                    {/* Bound to `debtor`, not `line0`: that is the column the list shows and the
                        bank matcher scores against. The address lines follow it. */}
                    <FormInput name="debtor" label={t('invoiceList.externalImport.fields.nameCompany')} />
                    <div className="tw-grid tw-gap-2 md:tw-grid-cols-2">
                        <FormInput name="line1" label={t('invoiceList.externalImport.fields.addressLine2')} />
                        <FormInput name="line2" label={t('invoiceList.externalImport.fields.addressLine3')} />
                        <FormInput name="postcode" label={t('invoiceList.externalImport.fields.postcode')} />
                        <FormInput name="city" label={t('invoiceList.externalImport.fields.city')} />
                    </div>
                    <div className="tw-grid tw-gap-2 md:tw-grid-cols-2">
                        <FormInput.Select
                            name="document_type"
                            label={t('invoiceList.externalImport.fields.documentType')}
                            disableCustomValue
                            options={documentTypeOptions}
                        />
                        <FormInput
                            name="invoice_number"
                            label={t('invoiceList.externalImport.fields.invoiceNumber')}
                        />
                    </div>
                    <div className="tw-grid tw-gap-2 md:tw-grid-cols-2">
                        <FormInput.DatePicker
                            name="document_date"
                            label={t('invoiceList.externalImport.fields.documentDate')}
                        />
                        <FormInput.DatePicker
                            name="due_date"
                            label={t('invoiceList.externalImport.fields.dueDate')}
                        />
                    </div>
                    <FormInput.Textarea
                        name="remarks"
                        rows={2}
                        label={t('invoiceList.externalImport.fields.description')}
                    />
                </FormDivider>

                <FormDivider title={t('belegAssistant.sections.categorisation')}>
                    <AccountFormInput
                        name="user_invoice_account_number"
                        label={t('invoiceList.externalImport.fields.category')}
                        labelRight="SK"
                    />
                    <div className="tw-grid tw-gap-2 md:tw-grid-cols-3">
                        <FormInput
                            className="md:tw-col-span-2"
                            name="value"
                            label={t('invoiceList.externalImport.fields.grossValue')}
                            type="number"
                            min={0}
                            step={0.01}
                        />
                        <FormInput.Select
                            name="currency"
                            label={t('invoiceList.externalImport.fields.currency')}
                            disableCustomValue
                            options={currencies.map((currency) => ({ key: currency.code, label: currency.name }))}
                        />
                    </div>
                    <FormInput
                        name="taxes"
                        label={t('invoiceList.externalImport.fields.taxes')}
                        type="number"
                        min={0}
                        step={1}
                    />
                    <FormInput.Select
                        name="tax_code"
                        label={t('invoiceList.externalImport.fields.taxCode')}
                        options={taxCodeOptions}
                    />
                    <div className="tw-grid tw-gap-2 md:tw-grid-cols-2">
                        <CostCenterFormInput
                            name="cost_1"
                            label={t('invoiceList.externalImport.fields.costCenter1')}
                        />
                        <CostCenterFormInput
                            name="cost_2"
                            label={t('invoiceList.externalImport.fields.costCenter2')}
                        />
                    </div>
                </FormDivider>

                {rail}
            </div>

            <div className="tw-shrink-0">
                <FormInput.Submit isLoading={isSubmitting}>{submitLabel}</FormInput.Submit>
            </div>
        </Form>
    );
};

export default BelegForm;
